import { VerifyAuthResponse, WorkspacePlan } from '@common/api/authService';
import {
    billingService, GetInvoicesResponse, GetUserPointsHistoryResponse,
    UserPoints
} from '@common/api/billingService';
import dayjs from 'dayjs';
import { useState } from 'react';

export interface UserBilling {
    userPlan: WorkspacePlan | null;
    userPoints: UserPoints | null;
    pointsHistory: GetUserPointsHistoryResponse | null;
    invoices: GetInvoicesResponse | null;
    loading: {
        plan: boolean;
        points: boolean;
        history: boolean;
        invoices: boolean;
    };
    error: UserBillingError;
    fetchUserPoints: () => Promise<void>;
    fetchPointsHistory: (page?: number, size?: number) => Promise<void>;
    fetchInvoices: (page?: number, size?: number) => Promise<void>;
    refreshAll: () => Promise<void>;
    planText: {
        planDisplayText: string;
        badge: string;
        expirationText: string;
        buttonText: string;
        status: 'trial' | 'active' | 'canceled' | 'expired' | 'none';
    };
    isExpired: boolean;
    isCanceled: boolean;
    isTrial: boolean;
}

export interface UserBillingError {
    plan: string | null;
    points: string | null;
    history: string | null;
    invoices: string | null;
}

export const useUserBilling = (profile: VerifyAuthResponse | null) => {
    const userPlan = profile?.workspacePlan || null;
    const [userPoints, setUserPoints] = useState<UserPoints | null>(null);
    const [pointsHistory, setPointsHistory] = useState<GetUserPointsHistoryResponse | null>(null);
    const [invoices, setInvoices] = useState<GetInvoicesResponse | null>(null);
    const [loading, setLoading] = useState({
        plan: false,
        points: false,
        history: false,
        invoices: false
    });
    const [error, setError] = useState({
        plan: null,
        points: null,
        history: null,
        invoices: null
    });

    // Fetch user plan
    // const fetchUserPlan = async () => {
    //     try {
    //         setLoading(prev => ({ ...prev, plan: true }));
    //         setError(prev => ({ ...prev, plan: null }));
    //         const response = await billingService.getUserPlans();
    //         setUserPlan(response);
    //         return response;
    //     } catch (err: any) {
    //         console.error('Failed to fetch user plan:', err);
    //         setError(prev => ({ ...prev, plan: err.message || 'Failed to fetch plan' }));
    //     } finally {
    //         setLoading(prev => ({ ...prev, plan: false }));
    //     }
    // };

    // Fetch user points
    const fetchUserPoints = async () => {
        try {
            setLoading(prev => ({ ...prev, points: true }));
            setError(prev => ({ ...prev, points: null }));
            const response = await billingService.getUserPoints();
            setUserPoints(response);
        } catch (err: any) {
            console.error('Failed to fetch user points:', err);
            setError(prev => ({ ...prev, points: err.message || 'Failed to fetch points' }));
        } finally {
            setLoading(prev => ({ ...prev, points: false }));
        }
    };

    // Fetch points history
    const fetchPointsHistory = async (page: number = 1, size: number = 5) => {
        try {
            setLoading(prev => ({ ...prev, history: true }));
            setError(prev => ({ ...prev, history: null }));
            const response = await billingService.getUserPointsHistory({ page, size });
            
            if (page === 1) {
                setPointsHistory(response);
            } else {
                setPointsHistory(prev => {
                    if (!prev) return response;
                    return {
                        ...response,
                        data: [...prev.data, ...response.data]
                    };
                });
            }
        } catch (err: any) {
            console.error('Failed to fetch points history:', err);
            setError(prev => ({ ...prev, history: err.message || 'Failed to fetch history' }));
        } finally {
            setLoading(prev => ({ ...prev, history: false }));
        }
    };

    const fetchInvoices = async (page: number = 1, size: number = 5) => {
        try {
            setLoading(prev => ({ ...prev, invoices: true }));
            setError(prev => ({ ...prev, invoices: null }));
            const response = await billingService.getInvoices({ page, size });
            
            if (page === 1) {
                setInvoices(response);
            } else {
                setInvoices(prev => {
                    if (!prev) return response;
                    return {
                        ...response,
                        invoices: [...prev.invoices, ...response.invoices]
                    };
                });
            }
        } catch (err: any) {
            console.error('Failed to fetch invoices:', err);
            setError(prev => ({ ...prev, invoices: err.message || 'Failed to fetch invoices' }));
        } finally {
            setLoading(prev => ({ ...prev, invoices: false }));
        }
    }

    // Refresh all data
    const refreshAll = async (): Promise<void> => {
        await Promise.all([
            // fetchUserPlan(),
            // fetchUserPoints(),
            // fetchPointsHistory(),
            fetchInvoices()
        ]);
    };

    // 计算订阅状态
    const isExpired = Boolean(userPlan?.currentPeriodEnd && new Date().getTime() > new Date(userPlan.currentPeriodEnd).getTime());
    const isCanceled = userPlan?.status === 'pending_cancellation';
    const isTrial = Boolean(userPlan?.trialEndAt && new Date(userPlan.trialEndAt).getTime() > new Date().getTime());

    const getPlanText = () =>{

        const formatDate = (dateStr: string) => {
            return dayjs(dateStr).format('YYYY-MM-DD');
        };

        let planDisplayText = '';
        let badge = '';
        let expirationText = '';
        let buttonText = '';
        let status: 'trial' | 'active' | 'canceled' | 'expired' | 'none' = 'none';

        // 如果没有用户计划，返回默认值
        if (!userPlan) {
            return {
                planDisplayText: '',
                badge: '',
                expirationText: '',
                buttonText: 'Manage',
                status
            };
        }

        // 场景3: 订阅已过期
        if (isExpired) {
            planDisplayText = 'Plan Expired';
            badge = '';
            expirationText = `Expired on ${formatDate(userPlan.currentPeriodEnd)}.`;
            buttonText = 'Upgrade';
            status = 'expired';
            return {
                planDisplayText,
                badge,
                expirationText,
                buttonText,
                status
            };
        }

        // 场景1: 订阅中（自动续订，试用展示 Trial）
        if (isTrial) {
            planDisplayText = 'Basic';
            badge = 'Trial';
            expirationText = `Will renew on ${formatDate(userPlan.trialEndAt)}.`;
            buttonText = 'Manage';
            status = 'active';
            return {
                planDisplayText,
                badge,
                expirationText,
                buttonText,
                status
            };
        }

        // 场景2和3: 订阅中（已取消续订 / 自动续订）
        planDisplayText = userPlan?.planName || 'Basic';

        badge = '';

        if (isCanceled && userPlan?.currentPeriodEnd) {
            // 场景2: 订阅中（已取消续订）
            const daysLeft = Math.ceil((new Date(userPlan.currentPeriodEnd).getTime() - new Date().getTime()) / (1000 * 60 * 60 * 24));
            expirationText = daysLeft < 7
                ? `${daysLeft} days left.`
                : `Expires on ${formatDate(userPlan.currentPeriodEnd)}.`;
            buttonText = 'Renew';
            status = 'canceled';
        } else if (userPlan?.currentPeriodEnd) {
            // 场景1: 订阅中（自动续订）
            expirationText = `Will renew on ${formatDate(userPlan.currentPeriodEnd)}.`;
            buttonText = 'Manage';
            status = 'active';
        } else {
            expirationText = '';
            buttonText = 'Manage';
            status = 'active';
        }

        return {
            planDisplayText,
            badge,
            expirationText,
            buttonText,
            status
        };
    }

    return {
        userPlan,
        // setUserPlan,
        userPoints,
        pointsHistory,
        invoices,
        loading,
        error,
        // fetchUserPlan,
        fetchUserPoints,
        fetchPointsHistory,
        fetchInvoices,
        refreshAll,
        planText: getPlanText(),
        isExpired,
        isCanceled,
        isTrial
    };
};
